import React, { createContext, useContext, useEffect, useState } from 'react';
import { type ClientConfig, defaultConfig } from '../config/clientConfig';

type Theme = 'dark' | 'light';

interface ThemeContextType {
    config: ClientConfig;
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
    config: defaultConfig,
    theme: 'dark',
    toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Helper to convert Hex to HSL
// Returns "h s% l%"
function hexToHSL(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return "222.2 47.4% 11.2%"; // Fallback
    
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s, l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    
    // H is 0-1, S is 0-1, L is 0-1. 
    // CSS hsl needs H in degrees (0-360), S and L in %.
    const hDeg = Math.round(h * 360 * 10) / 10; // 1 decimal place
    const sPct = Math.round(s * 100 * 10) / 10;
    const lPct = Math.round(l * 100 * 10) / 10;
    
    return `${hDeg} ${sPct}% ${lPct}%`;
}

export function ThemeProvider({ 
    children, 
    initialConfig = defaultConfig 
}: { 
    children: React.ReactNode;
    initialConfig?: ClientConfig;
}) {
    const [config] = useState<ClientConfig>(initialConfig);
    const [theme, setTheme] = useState<Theme>(() => {
        // 1. Check local storage
        const stored = localStorage.getItem('theme');
        if (stored === 'light' || stored === 'dark') return stored;
        
        // 2. Check system preference
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
        
        // 3. Default to light matching the new root variables
        return 'light';
    });

    useEffect(() => {
        const root = document.documentElement;
        
        // Apply or remove 'dark' class
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        
        // Save to local storage
        localStorage.setItem('theme', theme);

        // Convert brand color to HSL if config has one
        if (config.brandColor) {
             const hsl = hexToHSL(config.brandColor);
             root.style.setProperty('--primary', hsl);
             // Assume foreground is white/readable on primary for now
             root.style.setProperty('--primary-foreground', '0 0% 100%');
        }
    }, [theme, config]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    };

    return (
        <ThemeContext.Provider value={{ config, theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}
